<template>
    <div class="sub-card">
        <div class="rel cloud-plan-name cloud-plan-header">
            <div class="cloud-title">
                <div class="cloud-title-plan">
                    <span class="title">{{ $options.filters?.planName(subscribe) }}</span>
                    <reo-tip-dialog icon-width="18" isBottomWhenMobile>
                        <div class="main-info">
                            <p v-if="tipDialogText" v-html="tipDialogText"></p>
                        </div>
                        <div class="sub-info">
                            {{ $t("dashboard.storageRegion") }}:
                            {{ subscribe.storageRegion }}
                        </div>
                        <div class="sub-info">
                            {{ $t("dashboard.suitableDevices") }}:
                            {{ subscribe.productSupportedString }}
                        </div>
                        <router-link
                            v-if="subscribe.actionConfig?.cancel.visibility === 'visible'"
                            :class="cancelOptions.class"
                            :to="cancelOptions.to"
                            :ga-label="cancelOptions.gaLabel"
                        >
                            {{ cancelOptions.label}}
                        </router-link>
                    </reo-tip-dialog>
                </div>
                <div class='legacy-tip'
                    v-html="$t(
                        'dashboard.legacyPlan',
                        lang,
                        {
                            link: config.legacy_plan_support
                        }
                    )"
                    v-if='subscribe.legacyTag' />
            </div>
            <div class="glass_cloud">
                <img src="/src/common/images/reolink/dashboard/glass_cloud.png" alt="glass_cloud" />
            </div>
        </div>
        <dl class="cloud-plan-content">
            <dd v-if="subscribe.defaultVideoHistory !== 0">
                <label>{{ $t("dashboard.video_history") }}</label>
                <span class="time">{{ `${subscribe.defaultVideoHistory} ${$t("dashboard.days")}` }}</span>
            </dd>
            <dd class="progress-bar-dd" v-if="subscribe.maxSpace !== 0 && !isRichPlan">
                <div class="space-info-container">
                    <label id="storageSpace">{{ $t("dashboard.space") }}</label>
                    <span class="space-info">
                        {{ $n(subscribe.usedSpace, "decimal") }}G/{{ maxSpaceSize }}
                    </span>
                </div>
                <div class="progress-bar" v-if="!isUnlimitedPlan">
                    <div class="space">
                        <p class="use-space" :style="{ width: `${subscribe.percent}%` }"></p>
                    </div>
                </div>
            </dd>
            <dd id="cameraAssign" class="rel">
                <label>{{ $t("dashboard.cameras_assigned") }}</label>
                <span class="number"><span
                                         class="devicesLength"
                                         :class="{ blue: !isExpiredSub, disabled: isExpiredSub }"
                                         @click="handleShowSubDevices"
                                     >{{
                                         subscribe.deviceList.length }}</span>/{{ subscribe.maxCamera }}
                    <tip class="how-to-bind-tip" v-if="!FEATURE('main.how_to_bind')">
                        <div class="question-mod">
                            <p>
                                <a :href="config.howToBind" target="_blank">
                                    {{ $t("dashboard.how_to_bind") }}
                                    <span class="ui-icon icon-icon_arrow-right"></span>
                                </a>
                            </p>
                        </div>
                    </tip>
                </span>

            </dd>
            <dd>
                <label>{{ $t("dashboard.valid_time") }}</label>
                <span class="time">{{ $options.filters?.planValidTime(subscribe)}}</span>
            </dd>
            <dd v-if="isRichPlan && subscribe.dailyRichNotificationLimit > 0" class="rich-notification-limit">
                <label>{{ $t('dashboard.daily_rich_notification_limit') }}</label>
                <span :style="{ color: subscribe.dailyRichNotificationCount >= subscribe.dailyRichNotificationLimit ? '#FF4747' : '#00104A' }">
                    {{ subscribe.dailyRichNotificationCount }}/{{ subscribe.dailyRichNotificationLimit }}
                </span>
            </dd>
            <dd v-if="isRichPlan && subscribe.dailyRichNotificationLimit > 0 && switchPlanRoute" class="unlock-link-dd">
                <router-link :to="switchPlanRoute" class="ui-link unlock-link">
                    {{ $t('dashboard.unlock_unlimited_rich_notifications') }}
                </router-link>
            </dd>
            <dd class="auto-renew-dd"
                v-if="subscribe.actionConfig?.autoRenew.visibility === 'visible'">
                <AutoRenewSwitch :autoRenewal="subscribe.autoRenewal"
                    :next-billing-at="subscribe.nextBillingAt"
                    :subscription-id="subscribe.subId"
                    @refresh="$emit('refresh')"
                    @renew="renewSub"
                />
            </dd>
            <dd class="buttons">
                <div>
                    <ul class="button-content" v-if="planOptions().length">
                        <li v-for="(option, i) in planOptions()" :key="i">
                            <router-link :class="option.class" :to="option.to" :ga-label="option.gaLabel">{{ option.label
                            }}</router-link>
                        </li>
                    </ul>
                    <p class="renew-note red"
                        v-if="subscribe.planStatus === 'disabled'"
                        v-html="$t('dashboard.planDisabledNote', lang, {
                            link: config.plan_disabled_support
                        })"></p>
                </div>
            </dd>
        </dl>
    </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { ISubscribeData } from './types';
import { mapGetters } from 'vuex';
import { config } from '@/config/reolink';
import { IGetCloudProductsResponse } from '@reolink-cloud/interface';
import AutoRenewSwitch from '../AutoRenewSwitch.vue';
import Tip from '@/basic/Tip.vue';
import ReoTipDialog from '../ReoTipDialog.vue';
import { formatTime } from '@/utils/time';
import { checkIsRichPlan } from './utils';
import { isUnlimitedStoragePlan } from '@/utils/business';

export default Vue.extend({
    components: {
        AutoRenewSwitch,
        Tip,
        ReoTipDialog
    },
    props: {
        subscribe: Object as PropType<ISubscribeData>,
        cloudProductLists: Object as PropType<IGetCloudProductsResponse>,
        isActivePlans: Boolean
    },
    data() {

        return {
            showSubInfoDialog: false
        };

    },
    computed: {
        ...mapGetters( ['lang'] as const ),
        config: () => config,
        isRichPlan(): boolean {

            return checkIsRichPlan( this.subscribe );

        },
        switchPlanRoute(): Record<string, any> | null {

            if ( this.subscribe.actionConfig?.switch.visibility !== 'visible' ) return null;
            return {
                name: 'update-cloud',
                query: {
                    ps_id: this.subscribe.subId,
                    model: this.subscribe.portablePlanId !== '0'
                        ? this.cloudProductLists?.planSeries?.[this.cloudProductLists?.plans?.[this.subscribe.portablePlanId]?.seriesKey]?.deviceGroup
                        : undefined,
                    loc: this.subscribe.portablePlanId !== '0'
                        ? this.cloudProductLists?.planSeries?.[this.cloudProductLists?.plans?.[this.subscribe.portablePlanId]?.seriesKey]?.storageRegion
                        : undefined
                }
            };

        },
        aiFeatures(): readonly string[] {

            return config.aiFeatures;

        },
        tipDialogText(): string {

            const comma = this.$t( 'comma' ) as string;
            const items: string[] = [];

            if ( Number( this.subscribe.price ) === 0 ) {

                items.push( this.$t( 'plan.free' ) as string );

            }

            if ( !this.isRichPlan ) {

                const history = this.$options.filters?.planHistory?.( this.subscribe ) as string | undefined;
                if ( history ) {

                    items.push( history );

                }

            }

            const camera = this.$options.filters?.planCamera?.( this.subscribe ) as string | undefined;
            if ( camera ) {

                items.push( camera );

            }

            if ( !this.isRichPlan ) {

                const capacity = this.$options.filters?.planCapacity?.( this.subscribe ) as string | undefined;
                if ( capacity ) {

                    items.push( capacity );

                }

            }

            for ( const feature of this.aiFeatures ) {

                if ( this.subscribe.plan.style[`cloud_ai.${feature}`] ) {

                    items.push( this.$t( `cloud_ai.${feature}` ) as string );

                }

            }

            const richNotification = this.$options.filters?.planRichNotification?.( this.subscribe ) as string | undefined;
            if ( richNotification ) {

                items.push( richNotification );

            }

            const content = items.filter( Boolean ).join( comma + ' ' );
            return content ? `${content}.` : '';

        },
        cancelOptions(): Record<string, any> {

            return {

                to: {
                    path: `/user/cancel-plan/legacy`,
                    query: {
                        ps_id: this.subscribe.subId,
                        p_id: this.subscribe.plan.id
                    }
                },
                label: this.$t( 'dashboard.items.cancel' ),
                class: 'ui-link cancel-button ',
                gaLabel: 'Dashboard cancel'

            };

        },
        isUnlimitedPlan(): boolean {

            return isUnlimitedStoragePlan( this.subscribe.plan );

        },
        maxSpaceSize(): string {

            return this.isUnlimitedPlan ? this.$tc( 'plan.unlimited' ) : `${this.$n( this.subscribe.maxSpace, 'decimal' )}G`;

        },
        isExpiredSub(): boolean {

            return !this.isActivePlans;

        }
    },
    methods: {
        formatTime,
        handleShowSubDevices(): void {

            if ( this.isExpiredSub ) return;
            this.$emit( 'showSubDevices', this.subscribe );

        },
        planOptions(): any[] {

            let planOptions: any[] = [];

            if ( this.subscribe.actionConfig?.renew.visibility === 'visible' ) {

                let renewDes = {

                    to: {
                        path: '/checkout/',
                        query: {
                            ps_id: this.subscribe.subId,
                            p_id: this.subscribe.plan.id,
                            mode: 'renew'
                        }
                    },
                    label: this.$t( 'dashboard.renew' ),
                    class: 'ui-button primary-button short-button renew-button ',
                    gaLabel: 'Dashboard renew'
                };
                planOptions.push( renewDes );

            }

            if ( this.subscribe.actionConfig?.switch.visibility === 'visible' ) {

                planOptions.push({
                    to: {
                        name: 'update-cloud',
                        query: {
                            ps_id: this.subscribe.subId,
                            model: this.subscribe.portablePlanId !== '0' ? this.cloudProductLists.planSeries[this.cloudProductLists.plans[this.subscribe.portablePlanId].seriesKey].deviceGroup : undefined,
                            loc: this.subscribe.portablePlanId !== '0' ? this.cloudProductLists.planSeries[this.cloudProductLists.plans[this.subscribe.portablePlanId].seriesKey].storageRegion : undefined
                        }
                    },
                    label: this.$t( 'dashboard.items.switch' ),
                    class: 'ui-link cancel-button',
                    gaLabel: 'Dashboard switch'

                });

            }

            return planOptions;

        },
        async renewSub(): Promise<void> {

            await this.$router.push({
                path: '/checkout/',
                query: {
                    ps_id: this.subscribe.subId,
                    p_id: this.subscribe.plan.id,
                    mode: 'renew'
                }
            });

        }
    }
});
</script>

<style lang="less" scoped>

.sub-card {
    display: grid;
    grid-row: span 2;
    grid-template-rows: subgrid;
    width: 100%;
    height: 100%;

    @media @mobile {
        display: flex;
        flex-direction: column;
    }
}

.cloud-plan-name {
    background-image: url('/src/common/images/reolink/dashboard/card_header.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;
    padding: 24px 20px;
    border-radius: 8px 8px 0 0;
    position: relative;
    .glass_cloud {

        position: absolute;
        bottom: 0;
        right: 0;
        display: flex;
        pointer-events: none;
        img {
            width: 206px;
        }
    }
}

dl {
    text-align: left;
    padding: 16px 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
}

dl dd {
    display: flex;
    justify-content: space-between;
    gap: 8px;

    @media @mobile {

    }

    &#cameraAssign {
        @media @mobile {

        }
    }

    .cloud-title {
        display: flex;
        align-items: flex-start; /* 改为顶部对齐，让内容更好控制 */
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px;
    }
}

.legacy-tip {
    font-size: 16px;
    font-weight: 500;
    width: fit-content;

    /deep/ a {
        font-weight: 500;
    }

    @media @mobile {
        font-size: .24rem;
    }

    .support-link {
        font-weight: 500;
        color: #333;
        cursor: pointer;
        text-decoration: underline;
        text-decoration-thickness: 1px;
    }
}

label {
    display: inline-block;
    font-size: 16px;
    font-weight: 500;
    color: #030852;
    flex: 0 0 auto;
    margin-right: 0;
    line-height: 1.2;
    @media @mobile{
        font-size: 14px;
    }
}

span {
    font-size: 16px;
    font-weight: 400;
    color: #00104A;
    @media @mobile{
        font-size: 14px;
    }
}

dd+dd {
    margin-top: 12px;
}

.span {
    font-size: 0.16rem;
}

.title {
    font-size: 18px;
    font-weight: 600;
    color: #00104A;
    text-align: left;
}

.cloud-title-plan {
    text-align: start;
}

.reo-tip-dialog {
    margin-left: 4px;
}

.cloud-title-plan /deep/ .reo-tip-dialog .reo-dialog-inner {
    @media @pc {
        --dialog-padding-top: 20px;
        --dialog-padding-right: 20px;
        padding: 32px 64px;
    }
    @media @mobile {
        --dialog-padding-top: 16px;
        --dialog-padding-right: 12px;
        padding: 50px 20px;
    }
}

.cancel-button {
    text-align: center;
    background-color: #fafbfc;
}

.cloud-title i.cloud-title-color {
    width: 0.18rem;
    height: 0.18rem;
    border-radius: 50%;
    margin-right: 0.1rem;
    vertical-align: bottom;
    position: relative;

    @media @mobile {
        width: 0.24rem;
        height: 0.24rem;
    }
}

.cloud-title i.cloud-title-color.yellow {
    background-color: #ffac48;
}

.buttons {
    font-size: 0;
    margin-top: auto;
    flex-shrink: 0;

    .button-content {
        display: flex;
        gap: 32px;
        flex-wrap: wrap;

        li {
            display: inline-block;
            vertical-align: top;

            &.extra-style {
                position: relative;
                padding-bottom: 49px;
            }

        }

        .renew-button,
        .switch-button {
            font-size: 14px;
            padding: 10px 0;
            min-width: 130px;
            max-width: 132px;
            align-items: center;
            justify-content: center;
        }

    }

    .red {
        font-size: 14px;
        font-weight: 500;
        margin-top: 12px;
    }
}

.devicesLength {
    cursor: pointer;

    &.disabled {
        color: @font_disable;
        cursor: not-allowed;
    }
}

.space {
    background-color: @font_fourth;
    display: inline-block;
    width: 100%;
    height: 4px;
    vertical-align: middle;
}

.use-space {
    height: 100%;
    width: 65%;
    border-radius: 2rem;
    background-color: @theme;
}

.space-info {
    font-size: 16px;
    font-weight: 400;
    line-height: 1;
    vertical-align: middle;
    @media @mobile{
        font-size: 14px;
    }
}

.progress-bar {
    display: inline-flex;

    @media @mobile {
        display: flex;
        width: 100%;
    }

    .red {
        width: 100%;
        font-size: 0.14rem;
        line-height: 1.2;
        margin-top: 0.1rem;

        @media @mobile {
            font-size: 0.24rem;
            line-height: 1.4;
            margin-top: 0.2rem;

        }
    }
}

.auto-renew-dd {
    margin-top: 0;
    margin-bottom: 24px;
}

.rich-notification-limit {
    margin-top: 12px;
}

.unlock-link-dd {
    justify-content: flex-end;
    margin-top: 4px;
}

.unlock-link {
    font-size: 14px;
    line-height: 21px;
}

.progress-bar-dd {
    flex-wrap: wrap;
    flex-direction: column;
    gap: 12px;

    @media @mobile {
        flex-direction: column;
    }

    .red {
        width: 100%;
        font-size: 0.14rem;
        line-height: 1.2;
        margin-top: 0.1rem;

        @media @mobile {
            font-size: 0.24rem;
            line-height: 1.4;
            margin-top: 0.2rem;

        }
    }

    .space-info-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
}

.name-edit-part{
    font-size: 14px;
    font-weight: 500;
    color: @font_secondary;
    display: flex;
    align-items: center;
    column-gap: 8px;
    flex-wrap: wrap;
    p.sub-title{
        word-break: break-all;
    }
    @media @pc{
        p{
            display: inline-block;
        }
    }

    .edit-icon{
        color:@theme;
        cursor: pointer;
        width: 20px;
        height: 20px;
    }
}
#cameraAssign .number{
    display: flex;
    @media @mobile{
        margin-right: .1rem;
    }
}

#cameraAssign .reo-tip .content{
    @media @mobile{
        left: calc( -3.3rem + 50% );
    }
}

.blue{
    color: @theme;
}

@media @mobile {

    .cloud-plan {
        text-align: left;
    }

    .cloud-plan .no-plan {
        font-size: .26rem;
    }

    .cloud-plan-name .content {
        margin-left: -3rem;
    }

    .devices .status {
        width: 1.6rem;
    }

    label.renew-label {
        display: none;
    }

    label {
        width: 2.82rem;
        font-size: .26rem;
        margin-bottom: 0;
    }

    dd+dd {
        margin-top: 8px;
    }

    .title {
        font-size: 18px;
    }
    .buttons {
        margin-top: .4rem;

        &>div {
            display: flex;
            flex-direction: column;

            &>ul {
                display: flex;
                align-items: center;
            }

            .button-content {
                margin: 0 auto;
                width: 100%;
                text-align: center;

                li+li {
                    margin-left: .24rem;
                }

                .renew-button,
                .switch-button {
                    font-size: 14px;
                    line-height: .66rem;
                    padding: 0 0.36rem;

                    &:lang(es) {
                        padding: 0 .2rem;
                    }
                }

                .renew-button {
                    line-height: calc(.66rem - 2px);
                }

                .cancel-button {
                    line-height: .66rem;
                }

                .cancel-button {
                    font-size: 14px;
                    line-height: 1.6;
                    padding: 0;
                }
            }

            .red {
                font-size: .24rem;
                line-height: 1.4;
                margin-top: .2rem;
            }
        }

        .space-info {
            font-size: .26rem;
        }
    }
}

.main-info{
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 16px;
}

.sub-info{
    font-size: 14px;
    font-weight: 400;
}
.cancel-button {
    font-size: 16px;
    font-weight: 500;
    padding: 10px 0;
    display: inline-flex;

    &:hover {
        text-decoration: underline;
    }
}
.how-to-bind-tip {
    margin-left: 5px;
    align-items: center;
}
</style>
